import { ChartSpecBuilder } from './chart-specs.ts';
import type {
  ChartDataPoint,
  VegaLiteSpec,
  ChartType,
  ChartOptions,
  ChartRenderResult,
} from '../types/chart.ts';

/**
 * ChartRenderer - Utility for rendering Vega-Lite charts in the DOM
 *
 * This utility provides a consistent interface for rendering charts across the application,
 * handling Vega-Embed loading, error handling, and chart lifecycle management.
 */
export class ChartRenderer {
  private static vegaEmbed: typeof import('vega-embed') | null = null;

  /**
   * Render a chart in the specified DOM element
   */
  static async render(
    element: HTMLElement,
    data: ChartDataPoint[],
    chartType: ChartType,
    options: ChartOptions = {}
  ): Promise<ChartRenderResult> {
    try {
      if (!element || !element.isConnected) {
        return {
          success: false,
          error: 'Invalid or disconnected DOM element provided',
        };
      }

      // Clear previous content
      element.innerHTML = '';

      // Validate data
      if (!data || data.length === 0) {
        element.innerHTML = '<div class="text-gray-500 p-4">No data to display</div>';
        return {
          success: true,
          element,
        };
      }

      // Handle summary chart type specially - render as HTML instead of Vega
      if (chartType === 'summary') {
        return ChartRenderer.renderSummary(element, data, options);
      }

      // Calculate responsive dimensions if not provided
      const responsiveOptions = ChartRenderer.getResponsiveOptions(element, chartType, options);

      // Generate the appropriate chart specification
      const spec = ChartRenderer.getChartSpec(data, chartType, responsiveOptions);

      // Load vega-embed if not already loaded
      if (!ChartRenderer.vegaEmbed) {
        ChartRenderer.vegaEmbed = await import('vega-embed');
      }

      // Render the chart
      const result = await ChartRenderer.vegaEmbed.default(element, spec, {
        actions: false, // Hide the Vega-Lite actions menu
        padding: 5,
        renderer: 'svg', // Use SVG for better quality
      });

      // Attach click handler if provided
      if (options.onClick && result.view) {
        result.view.addEventListener('click', (_event: unknown, item: unknown) => {
          if (
            item &&
            typeof item === 'object' &&
            item !== null &&
            'datum' in item &&
            options.onClick
          ) {
            options.onClick((item as { datum: ChartDataPoint }).datum);
          }
        });
      }

      return {
        success: true,
        element,
        view: result.view,
      };
    } catch (error) {
      console.error('Error rendering chart:', error);

      // Display error message to user
      element.innerHTML = `
        <div class="text-red-500 p-4 border border-red-200 rounded">
          <h3 class="font-semibold mb-2">Error rendering chart</h3>
          <p class="text-sm">${error instanceof Error ? error.message : 'Unknown error occurred'}</p>
          <p class="text-xs mt-2 text-gray-600">Check the console for more details.</p>
        </div>
      `;

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        element,
      };
    }
  }

  /**
   * Render a summary display (large number with optional label)
   * Uses HTML/CSS instead of Vega-Lite for better text rendering
   */
  private static renderSummary(
    element: HTMLElement,
    data: ChartDataPoint[],
    options: ChartOptions = {}
  ): ChartRenderResult {
    const { xTitle = 'Total Count' } = options;

    // Calculate total from data
    const total = data.reduce((sum, item) => sum + item.value, 0);

    // Format the number with commas
    const formattedTotal = total.toLocaleString();

    // Always use xTitle as the label (it contains the summaryTitle if provided)
    const categoryLabel = xTitle;

    // Create the summary display with centered content
    element.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full py-12 px-6">
        <div class="text-center">
          <h3 class="text-gray-600 dark:text-gray-400 text-lg font-medium mb-4">
            ${categoryLabel}
          </h3>
          <div class="text-6xl font-bold text-gray-900 dark:text-white">
            ${formattedTotal}
          </div>
        </div>
      </div>
    `;

    return {
      success: true,
      element,
    };
  }

  /**
   * Calculate responsive dimensions based on container size
   */
  private static getResponsiveOptions(
    element: HTMLElement,
    chartType: ChartType,
    options: ChartOptions
  ): ChartOptions {
    // If width and height are already provided, use them
    if (options.width && options.height) {
      return options;
    }

    // Get container dimensions
    const containerRect = element.getBoundingClientRect();
    const availableWidth = containerRect.width || 300;
    const availableHeight = containerRect.height || 300;

    // For donut/pie charts, use square dimensions based on smallest side
    // Use 'container' for width to let Vega handle responsive sizing
    if (chartType === 'donut' || chartType === 'pie') {
      const size = Math.min(availableWidth, availableHeight);

      // For donut charts, maintain uniform ring thickness regardless of size
      // Calculate innerRadius by subtracting a fixed thickness from the radius
      // This keeps the click target area consistent while the overall chart scales
      const ringThickness = 60; // Fixed ring thickness in pixels
      const innerRadius =
        options.innerRadius ?? (chartType === 'donut' ? Math.max(size / 2 - ringThickness, 20) : 0);

      return {
        ...options,
        width: 'container' as unknown as number, // Let Vega handle responsive width
        height: size,
        innerRadius,
      };
    }

    // For other chart types, use 'container' width and available height
    return {
      ...options,
      width: options.width ?? ('container' as unknown as number),
      height: options.height ?? availableHeight,
    };
  }

  /**
   * Get the appropriate chart specification based on type and options
   */
  private static getChartSpec(
    data: ChartDataPoint[],
    chartType: ChartType,
    options: ChartOptions
  ): VegaLiteSpec {
    switch (chartType) {
      case 'bar':
        return ChartSpecBuilder.bar(data, options);

      case 'horizontalBar':
        return ChartSpecBuilder.horizontalBar(data, options);

      case 'line':
        return ChartSpecBuilder.line(data, options);

      case 'pie':
        return ChartSpecBuilder.pie(data, options);

      case 'donut':
        return ChartSpecBuilder.donut(data, options);

      case 'area':
        return ChartSpecBuilder.area(data, options);

      case 'summary':
        return ChartSpecBuilder.summary(data, options);

      default:
        console.warn(`Unknown chart type: ${chartType}, falling back to bar chart`);
        return ChartSpecBuilder.bar(data, options);
    }
  }

  /**
   * Update an existing chart with new data
   */
  static async update(
    element: HTMLElement,
    data: ChartDataPoint[],
    chartType: ChartType,
    options: ChartOptions = {}
  ): Promise<ChartRenderResult> {
    // For now, we'll just re-render the entire chart
    // In the future, we could implement more efficient updates
    return ChartRenderer.render(element, data, chartType, options);
  }

  /**
   * Clear a chart element
   */
  static clear(element: HTMLElement) {
    if (element) {
      element.innerHTML = '';
    }
  }

  /**
   * Get recommended chart type based on data characteristics
   */
  static getRecommendedChartType(data: ChartDataPoint[]): ChartType {
    if (data.length === 0) {
      return 'bar';
    }

    // If we have many categories (> 10), suggest horizontal bar
    if (data.length > 10) {
      return 'horizontalBar';
    }

    // If we have 2-8 categories, pie chart works well
    if (data.length >= 2 && data.length <= 8) {
      return 'pie';
    }

    // Check if categories look like dates/time series
    const hasDateLikeCategories = data.some(
      (item) =>
        /\d{4}-\d{2}-\d{2}/.test(item.category) || // YYYY-MM-DD
        /\d{4}-\d{2}/.test(item.category) || // YYYY-MM
        /Week of/.test(item.category) // Week of YYYY-MM-DD
    );

    if (hasDateLikeCategories) {
      return 'line';
    }

    // Default to bar chart
    return 'bar';
  }

  /**
   * Get chart type options for UI selection
   */
  static getAvailableChartTypes(): Array<{ key: ChartType; label: string; description: string }> {
    return [
      {
        key: 'bar',
        label: 'Bar Chart',
        description: 'Vertical bars, good for comparing categories',
      },
      {
        key: 'horizontalBar',
        label: 'Horizontal Bar Chart',
        description: 'Horizontal bars, good for many categories or long labels',
      },
      {
        key: 'line',
        label: 'Line Chart',
        description: 'Connected points, ideal for time series data',
      },
      {
        key: 'pie',
        label: 'Pie Chart',
        description: 'Circular segments, shows parts of a whole',
      },
      {
        key: 'donut',
        label: 'Donut Chart',
        description: 'Pie chart with center removed, modern look',
      },
      {
        key: 'area',
        label: 'Area Chart',
        description: 'Filled area under line, emphasizes magnitude',
      },
      {
        key: 'summary',
        label: 'Summary',
        description: 'Large number display, shows total count',
      },
    ];
  }

  /**
   * Validate chart configuration
   */
  static validateConfiguration(
    data: ChartDataPoint[],
    chartType: ChartType,
    options: ChartOptions
  ): { isValid: boolean; warnings: string[]; errors: string[] } {
    const warnings: string[] = [];
    const errors: string[] = [];

    // Validate data
    if (!data || data.length === 0) {
      errors.push('No data provided for chart');
    }

    // Validate chart type
    const availableTypes = ChartRenderer.getAvailableChartTypes().map((t) => t.key);
    if (!availableTypes.includes(chartType)) {
      errors.push(`Invalid chart type: ${chartType}`);
    }

    // Type-specific validations
    if (chartType === 'pie' || chartType === 'donut') {
      if (data.length > 12) {
        warnings.push(
          'Pie charts become hard to read with many categories. Consider using a bar chart.'
        );
      }

      const hasNegativeValues = data.some((item) => item.value < 0);
      if (hasNegativeValues) {
        errors.push('Pie charts cannot display negative values');
      }
    }

    // Validate dimensions
    if (options.width && options.width < 100) {
      warnings.push('Chart width is very small and may not render properly');
    }

    if (options.height && options.height < 100) {
      warnings.push('Chart height is very small and may not render properly');
    }

    return {
      isValid: errors.length === 0,
      warnings,
      errors,
    };
  }

  /**
   * Export chart as image (requires chart to be rendered)
   */
  static async exportAsImage(
    element: HTMLElement,
    format: 'png' | 'svg' = 'png'
  ): Promise<string | null> {
    try {
      const svgElement = element.querySelector('svg');
      if (!svgElement) {
        throw new Error('No SVG element found in chart container');
      }

      if (format === 'svg') {
        // Return SVG as string
        return new XMLSerializer().serializeToString(svgElement);
      }

      // Convert SVG to PNG
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load SVG as image'));
        };
        img.src = url;
      });
    } catch (error) {
      console.error('Error exporting chart as image:', error);
      return null;
    }
  }
}
